import { useEffect, useState, type ReactNode } from "react";
import { affectsCars, driveText, ROAD_LABEL } from "../../../shared/drive";
import { resortById } from "../../../shared/mountains";
import { useMountains } from "../../data/newsQueries";
import { DirectionsButton } from "../../ui/Directions";
import { Icon } from "../../ui/Icon";
import { openOutside, QrCode } from "../../ui/OutsideLink";
import { Sheet } from "../../ui/Sheet";
import { ResortLogo } from "./ResortLogo";
import "./outdoors.css";

// A mountain's snow report inside Kinmantle: our own summary (gauge, forecast,
// drive, roads) on top and the resort's report page in a frame below. Pages
// that refuse to be framed (Resort.frameable) get a QR code instead.

const EVENT = "km:snow-report";

export function openSnowReport(resortId: string) {
  window.dispatchEvent(new CustomEvent<string>(EVENT, { detail: resortId }));
}

const inches = (n: number | null | undefined) => (n == null ? "—" : `${n}″`);

function Stat({ label, value, sub, big }: { label: string; value: ReactNode; sub?: string; big?: boolean }) {
  return (
    <div className={`snow-stat ${big ? "is-big" : ""}`}>
      <b>{value}</b>
      <span>{label}</span>
      {sub && <small className="muted">{sub}</small>}
    </div>
  );
}

function SnowReportSheet({ id, onClose }: { id: string; onClose: () => void }) {
  const resort = resortById(id);
  const q = useMountains();
  const [loaded, setLoaded] = useState(false);
  if (!resort) return null;
  const r = q.data?.resorts.find((x) => x.id === id);
  const roads = (q.data?.roads ?? []).filter((e) => e.resorts.includes(id) && affectsCars(e));
  const site = resort.report.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");

  return (
    <Sheet
      title={`${resort.name} snow report`}
      onClose={onClose}
      size="full"
      footer={
        <button className="btn btn-primary" onClick={onClose}>
          Done
        </button>
      }
    >
      <div className="snow-sheet">
        <section className={`snow-summary ${r?.powder ? `is-powder-${r.powder}` : ""}`} aria-label="Kinmantle’s summary">
          <div className="snow-head">
            <ResortLogo r={resort} />
            <div>
              <strong>{resort.name}</strong>
              {r?.powder && <span className="mtn-badge">{r.powder === "today" ? "Powder day" : "Powder tomorrow"}</span>}
              <small className="muted">Gauge: {resort.snotelName} (USDA SNOTEL) · forecast: NWS</small>
            </div>
          </div>
          <div className="snow-stats">
            <Stat big label="new in 24 h" value={inches(r?.newSnow24In)} />
            <Stat label="new in 48 h" value={inches(r?.newSnow48In)} />
            <Stat label="depth at the gauge" value={inches(r?.depthIn)} />
            <Stat label="forecast next 24 h" value={r?.forecast24In == null ? "—" : `${r.forecast24In}″`} sub={r?.forecast48In == null ? undefined : `${r.forecast48In}″ in 48 h`} />
            <Stat label="drive from home" value={driveText(r?.driveMin ?? null) ?? "—"} sub="without traffic" />
          </div>
          <div className="snow-roads">
            {roads.length ? (
              <ul className="dir-roads" aria-label="On the way">
                {roads.map((e) => (
                  <li key={e.id} className={`mtn-road is-${e.kind}`}>
                    <span className="mtn-road-kind">{ROAD_LABEL[e.kind]}</span>
                    <strong>{e.route}</strong>
                    <span className="mtn-road-where">{e.where ?? e.title}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="muted">{q.data ? "No chain laws or closures on the way" : ""}</span>
            )}
            <span className="snow-actions">
              <DirectionsButton target={{ name: resort.name, lat: resort.lat, lon: resort.lon }} className="btn" />
              <button className="btn" onClick={() => openOutside(resort.report, `${resort.name} snow report`)}>
                <Icon name="phone" size="1rem" /> On your phone
              </button>
            </span>
          </div>
        </section>
        {resort.frameable === false ? (
          <section className="snow-noframe">
            <QrCode text={resort.report} label={`QR code for the ${resort.name} snow report`} />
            <div>
              <p>
                {resort.name}’s site doesn’t allow its report to be shown inside other apps. Point your phone’s camera at the code to open it there.
              </p>
              <p className="outside-url">{site}</p>
            </div>
          </section>
        ) : (
          <div className="snow-frame">
            {!loaded && <div className="frame-wait">Loading {resort.name}’s report…</div>}
            <iframe src={resort.report} title={`${resort.name}’s own snow report`} sandbox="allow-scripts allow-same-origin allow-forms" referrerPolicy="no-referrer" onLoad={() => setLoaded(true)} />
          </div>
        )}
      </div>
    </Sheet>
  );
}

export function SnowReportHost() {
  const [id, setId] = useState<string | null>(null);
  useEffect(() => {
    const on = (e: Event) => setId((e as CustomEvent<string>).detail);
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);
  return id ? <SnowReportSheet key={id} id={id} onClose={() => setId(null)} /> : null;
}
